import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Palette, Users, ShoppingBag, Truck, Save, Send, BellRing, Plus, Trash2 } from "lucide-react";

const EMPTY_ADDR = { name: "", company: "", street1: "", street2: "", city: "", state: "", zip: "", country: "US", phone: "", email: "" };
const EMPTY_PKG = { weight_oz: 4, length_in: 10, width_in: 7, height_in: 3, predefined_package: "" };

export default function AdminShippingSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shipFrom, setShipFrom] = useState(EMPTY_ADDR);
  const [pkg, setPkg] = useState(EMPTY_PKG);
  const [freeThreshold, setFreeThreshold] = useState("100.00");
  const [shippoMode, setShippoMode] = useState(null);
  const [notifyEmails, setNotifyEmails] = useState([""]);
  const [savingNotify, setSavingNotify] = useState(false);
  const [testingNotify, setTestingNotify] = useState(false);

  useEffect(() => {
    api.get("/admin/shipping/settings").then((r) => {
      const d = r.data || {};
      // Coerce every optional string field to "" so controlled inputs never receive null
      const sf = { ...EMPTY_ADDR, ...(d.ship_from || {}) };
      for (const k of Object.keys(sf)) if (sf[k] == null) sf[k] = "";
      setShipFrom(sf);
      const pk = { ...EMPTY_PKG, ...(d.package || {}) };
      for (const k of Object.keys(pk)) if (pk[k] == null) pk[k] = "";
      setPkg(pk);
      setFreeThreshold(d.free_shipping_threshold_cents == null ? "" : (d.free_shipping_threshold_cents / 100).toFixed(2));
      setShippoMode(d.shippo_mode || null);
    }).catch(() => toast.error("Couldn't load shipping settings")).finally(() => setLoading(false));

    api.get("/admin/notification-settings").then((r) => {
      const list = r.data?.order_notify_emails || [];
      setNotifyEmails(list.length ? list : [""]);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ship_from: { ...shipFrom, street2: shipFrom.street2 || null, company: shipFrom.company || null, phone: shipFrom.phone || null, email: shipFrom.email || null },
        package: {
          weight_oz: parseFloat(pkg.weight_oz) || 4,
          length_in: parseFloat(pkg.length_in) || 10,
          width_in: parseFloat(pkg.width_in) || 7,
          height_in: parseFloat(pkg.height_in) || 3,
          predefined_package: pkg.predefined_package || null,
        },
        free_shipping_threshold_cents: freeThreshold === "" ? null : Math.max(0, Math.round(parseFloat(freeThreshold) * 100)),
      };
      await api.put("/admin/shipping/settings", payload);
      toast.success("Shipping settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveNotify = async () => {
    setSavingNotify(true);
    try {
      const cleaned = notifyEmails.map((e) => e.trim()).filter(Boolean);
      const { data } = await api.put("/admin/notification-settings", { order_notify_emails: cleaned });
      const list = data?.order_notify_emails || [];
      setNotifyEmails(list.length ? list : [""]);
      toast.success(list.length ? `Alerts will send to ${list.length} address${list.length > 1 ? "es" : ""}` : "Order alerts disabled");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingNotify(false);
    }
  };

  const sendTestAlert = async () => {
    setTestingNotify(true);
    try {
      const { data } = await api.post("/admin/notification-settings/test");
      toast.success(`Test alert sent to ${data.sent} address${data.sent === 1 ? "" : "es"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save the recipients first, then try again");
    } finally {
      setTestingNotify(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("jenova_admin_token");
    navigate("/admin/login");
  };

  return (
    <div className="pt-24 md:pt-28 pb-24 min-h-screen bg-white">
      <div className="max-w-[900px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between border-b border-[#E5E2DC] pb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-stone-500 font-semibold">Studio</p>
            <h1 className="mt-2 font-serif text-4xl md:text-5xl font-light tracking-tight">
              Shipping <em className="italic text-stone-600">settings</em>.
            </h1>
            <div className="mt-4 flex flex-wrap gap-4 text-sm">
              <Link to="/admin" className="text-stone-500 hover:text-[#1A1C1A] pb-1 inline-flex items-center gap-1.5">
                <ArrowLeft strokeWidth={1.5} className="w-4 h-4" /> Products
              </Link>
              <Link to="/admin/site" className="text-stone-500 hover:text-[#1A1C1A] pb-1 inline-flex items-center gap-1.5">
                <Palette strokeWidth={1.5} className="w-4 h-4" /> Site Content
              </Link>
              <Link to="/admin/founding" className="text-stone-500 hover:text-[#1A1C1A] pb-1 inline-flex items-center gap-1.5">
                <Users strokeWidth={1.5} className="w-4 h-4" /> Founding Members
              </Link>
              <Link to="/admin/orders" className="text-stone-500 hover:text-[#1A1C1A] pb-1 inline-flex items-center gap-1.5">
                <ShoppingBag strokeWidth={1.5} className="w-4 h-4" /> Orders
              </Link>
              <span className="text-[#1A1C1A] border-b-2 border-[#1A1C1A] pb-1 inline-flex items-center gap-1.5">
                <Truck strokeWidth={1.5} className="w-4 h-4" /> Shipping
              </span>
              <Link to="/admin/broadcast" className="text-stone-500 hover:text-[#1A1C1A] pb-1 inline-flex items-center gap-1.5">
                <Send strokeWidth={1.5} className="w-4 h-4" /> Broadcast
              </Link>
            </div>
          </div>
          <Button onClick={logout} variant="outline" className="rounded-full">
            <LogOut className="w-4 h-4 mr-1" strokeWidth={1.5} /> Log Out
          </Button>
        </div>

        {loading ? (
          <div className="mt-16 text-center text-stone-500">Loading…</div>
        ) : (
          <div className="mt-10 space-y-10">
            {/* Shippo mode banner */}
            {shippoMode === "test" && (
              <div className="p-4 rounded-xl bg-[#F4E9D8] border border-[#B8935A] text-[#5A4020]" data-testid="settings-test-mode-banner">
                <p className="font-semibold text-sm">Shippo is in TEST MODE</p>
                <p className="text-xs mt-1 leading-relaxed">
                  Every label you buy right now prints with a big <strong>"SAMPLE — DO NOT MAIL"</strong> watermark and cannot be scanned by USPS.
                  This is Shippo's safety feature so you can practice for free.
                  When you're ready to ship real packages: (1) add billing at <a className="underline" href="https://apps.goshippo.com/settings/billing" target="_blank" rel="noreferrer">apps.goshippo.com/settings/billing</a>,
                  then (2) paste your <code className="bg-[#EFDBB8] px-1 rounded">shippo_live_...</code> key to your developer and it goes live.
                </p>
              </div>
            )}
            {shippoMode === "live" && (
              <div className="p-3 rounded-xl bg-[#EAE7DC] border border-[#3F5F3F]/40 text-[#3F5F3F] text-sm" data-testid="settings-live-mode-banner">
                <p className="font-semibold">Shippo is in LIVE MODE</p>
                <p className="text-xs mt-1">Labels are real, mailable, and each purchase charges your Shippo balance.</p>
              </div>
            )}
            {shippoMode === "not_configured" && (
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                <p className="font-semibold">Shippo is not configured</p>
                <p className="text-xs mt-1">Add <code>SHIPPO_API_KEY</code> to the backend so rates and labels work.</p>
              </div>
            )}
            {/* Ship-from address */}
            <section className="bg-white border border-[#E5E2DC] rounded-xl p-8">
              <h2 className="font-serif text-2xl font-light">Ship-from address</h2>
              <p className="text-sm text-stone-500 mt-1">This is where every package leaves from. It appears as the return address on your labels.</p>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Name</Label><Input data-testid="ship-from-name" value={shipFrom.name} onChange={(e) => setShipFrom({ ...shipFrom, name: e.target.value })} placeholder="Amanda" /></div>
                <div><Label>Company (optional)</Label><Input data-testid="ship-from-company" value={shipFrom.company} onChange={(e) => setShipFrom({ ...shipFrom, company: e.target.value })} placeholder="Jenova Collective" /></div>
                <div className="md:col-span-2"><Label>Street address</Label><Input data-testid="ship-from-street1" value={shipFrom.street1} onChange={(e) => setShipFrom({ ...shipFrom, street1: e.target.value })} placeholder="123 Main St" /></div>
                <div className="md:col-span-2"><Label>Apartment / Suite (optional)</Label><Input data-testid="ship-from-street2" value={shipFrom.street2} onChange={(e) => setShipFrom({ ...shipFrom, street2: e.target.value })} placeholder="Unit 4B" /></div>
                <div><Label>City</Label><Input data-testid="ship-from-city" value={shipFrom.city} onChange={(e) => setShipFrom({ ...shipFrom, city: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>State</Label><Input data-testid="ship-from-state" value={shipFrom.state} onChange={(e) => setShipFrom({ ...shipFrom, state: e.target.value })} placeholder="CA" maxLength={2} /></div>
                  <div><Label>ZIP</Label><Input data-testid="ship-from-zip" value={shipFrom.zip} onChange={(e) => setShipFrom({ ...shipFrom, zip: e.target.value })} placeholder="90210" /></div>
                </div>
                <div><Label>Country</Label><Input data-testid="ship-from-country" value={shipFrom.country} onChange={(e) => setShipFrom({ ...shipFrom, country: e.target.value })} maxLength={2} /></div>
                <div><Label>Phone <span className="text-red-600">*</span></Label><Input data-testid="ship-from-phone" value={shipFrom.phone} onChange={(e) => setShipFrom({ ...shipFrom, phone: e.target.value })} placeholder="Required by USPS" /></div>
                <div className="md:col-span-2"><Label>Return email <span className="text-red-600">*</span></Label><Input data-testid="ship-from-email" type="email" value={shipFrom.email || ""} onChange={(e) => setShipFrom({ ...shipFrom, email: e.target.value })} placeholder="Required by USPS · e.g. hello@jenovacollective.co" /></div>
              </div>
            </section>

            {/* Default package */}
            <section className="bg-white border border-[#E5E2DC] rounded-xl p-8">
              <h2 className="font-serif text-2xl font-light">Default package</h2>
              <p className="text-sm text-stone-500 mt-1">Used when you don't set custom dimensions on an order. Weight also acts as the fallback for products without a per-item weight.</p>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><Label>Weight (oz)</Label><Input data-testid="pkg-weight" type="number" step="0.1" value={pkg.weight_oz} onChange={(e) => setPkg({ ...pkg, weight_oz: e.target.value })} /></div>
                <div><Label>Length (in)</Label><Input data-testid="pkg-length" type="number" step="0.1" value={pkg.length_in} onChange={(e) => setPkg({ ...pkg, length_in: e.target.value })} /></div>
                <div><Label>Width (in)</Label><Input data-testid="pkg-width" type="number" step="0.1" value={pkg.width_in} onChange={(e) => setPkg({ ...pkg, width_in: e.target.value })} /></div>
                <div><Label>Height (in)</Label><Input data-testid="pkg-height" type="number" step="0.1" value={pkg.height_in} onChange={(e) => setPkg({ ...pkg, height_in: e.target.value })} /></div>
              </div>
            </section>

            {/* Free shipping */}
            <section className="bg-white border border-[#E5E2DC] rounded-xl p-8">
              <h2 className="font-serif text-2xl font-light">Free shipping threshold</h2>
              <p className="text-sm text-stone-500 mt-1">Orders at or above this subtotal get free shipping. Leave blank to disable.</p>
              <div className="mt-6 max-w-xs">
                <Label>Threshold (USD)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">$</span>
                  <Input
                    data-testid="free-ship-threshold"
                    type="number"
                    step="0.01"
                    min="0"
                    value={freeThreshold}
                    onChange={(e) => setFreeThreshold(e.target.value)}
                    placeholder="100.00"
                    className="pl-7"
                  />
                </div>
              </div>
            </section>

            {/* Order alerts */}
            <section className="bg-white border border-[#E5E2DC] rounded-xl p-8" data-testid="order-alerts-section">
              <div className="flex items-start gap-3">
                <BellRing strokeWidth={1.5} className="w-5 h-5 text-[#1A1C1A] mt-1" />
                <div>
                  <h2 className="font-serif text-2xl font-light">Order alerts to your phone</h2>
                  <p className="text-sm text-stone-500 mt-1">
                    We'll email every address you list here the moment a real order is paid — set your phone's email
                    (Gmail, iCloud, Outlook) to push notifications and you'll get pinged in seconds.
                  </p>
                </div>
              </div>
              <div className="mt-6 space-y-3">
                {notifyEmails.map((email, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      data-testid={`notify-email-${i}`}
                      type="email"
                      placeholder="your.phone.inbox@gmail.com"
                      value={email}
                      onChange={(e) => {
                        const next = [...notifyEmails];
                        next[i] = e.target.value;
                        setNotifyEmails(next);
                      }}
                    />
                    {notifyEmails.length > 1 && (
                      <Button
                        data-testid={`notify-email-remove-${i}`}
                        type="button"
                        variant="outline"
                        size="icon"
                        className="rounded-full flex-shrink-0"
                        onClick={() => setNotifyEmails(notifyEmails.filter((_, idx) => idx !== i))}
                        aria-label="Remove"
                      >
                        <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button
                    data-testid="notify-email-add"
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={() => setNotifyEmails([...notifyEmails, ""])}
                  >
                    <Plus strokeWidth={1.5} className="w-4 h-4 mr-1" /> Add another
                  </Button>
                  <Button
                    data-testid="notify-save"
                    type="button"
                    onClick={saveNotify}
                    disabled={savingNotify}
                    size="sm"
                    className="bg-[#1A1C1A] hover:bg-[#3C3E3C] rounded-full"
                  >
                    <Save strokeWidth={1.5} className="w-4 h-4 mr-1" /> {savingNotify ? "Saving…" : "Save alerts"}
                  </Button>
                  <Button
                    data-testid="notify-test"
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={sendTestAlert}
                    disabled={testingNotify}
                  >
                    <Send strokeWidth={1.5} className="w-4 h-4 mr-1" /> {testingNotify ? "Sending…" : "Send test alert"}
                  </Button>
                </div>
                <p className="text-xs text-stone-500 leading-relaxed pt-2">
                  Tip: use your personal email (the one already synced to your phone's mail app). Save first, then hit
                  <em> Send test alert</em> to confirm it reaches your lock screen.
                </p>
              </div>
            </section>

            <div className="flex justify-end">
              <Button data-testid="save-shipping-settings" onClick={save} disabled={saving} className="bg-[#1A1C1A] hover:bg-[#3C3E3C] rounded-full">
                <Save className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
